import { create } from 'zustand';
import type { DaySchedule } from '../api/capacity.api';

// ─── Types ────────────────────────────────────────────────────────────────────

export type WorkloadDisplayMode = 'hours' | 'percent';

export interface RawWorklogEntry {
  id: string;
  mondayItemId: string;
  mondayBoardId: string;
  mondayUserId: string;
  date: string;
  hours: number;
  description: string | null;
  createdByUserId?: string | null;
}

/** Which workload indicator is active; null = off */
export type WorkloadIndicatorType = 'workload' | 'issues' | 'availability' | 'remaining_estimate' | 'worklog_workload';

export interface WorkloadEntry {
  /** monday.com user ID */
  userId: number;
  /** ISO date string (yyyy-MM-dd) */
  date: string;
  /** Total scheduled hours on this date */
  scheduledHours: number;
  /** Capacity hours on this date (from capacity scheme) */
  capacityHours: number;
  /** True if this date is a holiday for the user */
  isHoliday: boolean;
  /** Hours deducted due to holiday (full capacity on holiday days) */
  holidayHours: number;
  /** True if user has an approved leave on this date */
  isLeave: boolean;
  /** Leave day type: 'full', 'am', 'pm', or null if no leave */
  leaveDayType: 'full' | 'am' | 'pm' | null;
  /** Hours deducted due to leave (e.g. 8 for full-day, 4 for half-day on 8h schedule) */
  leaveHours: number;
  /** Available hours after holiday and leave deductions (capacityHours - holidayHours - leaveHours) */
  availableHours: number;
  /** True when items exist on a full-day leave (hours redistributed but conflict persists) */
  hasLeaveConflict: boolean;
  /** Hours logged via worklogs on this date (for remaining_estimate / worklog_workload modes) */
  worklogHours?: number;
  /** True for past days in remaining_estimate mode (UI renders "–") */
  isPastDay?: boolean;
}

export interface IssueCountEntry {
  /** monday.com user ID */
  userId: number;
  /** ISO date string (yyyy-MM-dd) */
  date: string;
  /** Number of items scheduled on this date */
  issueCount: number;
}

// ─── Persistence helpers ──────────────────────────────────────────────────────

const STORAGE_KEY = 'rp_workload_state';

/** Default indicator mode used for new users and as the validation fallback. */
export const DEFAULT_INDICATOR_TYPE: WorkloadIndicatorType = 'remaining_estimate';

/**
 * Selectable indicator modes. `worklog_workload` is intentionally absent — it is a
 * deprecated mode, hidden from the selector and migrated away on load (see
 * {@link normalizeIndicatorType}). `null` means the workload overlay is off.
 */
const VALID_INDICATOR_TYPES: (WorkloadIndicatorType | null)[] = ['workload', 'issues', 'availability', 'remaining_estimate', null];
const VALID_DISPLAY_MODES: WorkloadDisplayMode[] = ['hours', 'percent'];

/**
 * Normalize a persisted/restored indicator value: migrate the deprecated
 * `worklog_workload` mode to remaining estimate, preserve `null` (overlay off),
 * and fall back to the default for any unknown value.
 */
export function normalizeIndicatorType(value: unknown): WorkloadIndicatorType | null {
  if (value === 'worklog_workload') return DEFAULT_INDICATOR_TYPE; // migrate deprecated mode
  if (VALID_INDICATOR_TYPES.includes(value as WorkloadIndicatorType | null)) {
    return value as WorkloadIndicatorType | null;
  }
  return DEFAULT_INDICATOR_TYPE;
}

interface PersistedWorkloadState {
  indicatorType: WorkloadIndicatorType | null;
  displayMode: WorkloadDisplayMode;
}

function loadPersistedState(): { indicatorType: WorkloadIndicatorType | null; displayMode: WorkloadDisplayMode } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistedWorkloadState;
      const indicatorType = normalizeIndicatorType(parsed.indicatorType);
      const displayMode = VALID_DISPLAY_MODES.includes(parsed.displayMode) ? parsed.displayMode : 'hours';
      // Rewrite storage if we migrated a deprecated value, so it doesn't linger.
      if (indicatorType !== parsed.indicatorType) {
        persistState(indicatorType, displayMode);
      }
      return { indicatorType, displayMode };
    }
  } catch { /* ignore */ }
  return { indicatorType: DEFAULT_INDICATOR_TYPE, displayMode: 'hours' };
}

function persistState(indicatorType: WorkloadIndicatorType | null, displayMode: WorkloadDisplayMode) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ indicatorType, displayMode } as PersistedWorkloadState));
  } catch { /* ignore */ }
}

// ─── Index helpers ────────────────────────────────────────────────────────────

function buildEntriesByUser(entries: WorkloadEntry[]): Map<number, WorkloadEntry[]> {
  const map = new Map<number, WorkloadEntry[]>();
  for (const entry of entries) {
    let arr = map.get(entry.userId);
    if (!arr) {
      arr = [];
      map.set(entry.userId, arr);
    }
    arr.push(entry);
  }
  return map;
}

function buildIssuesByUser(entries: IssueCountEntry[]): Map<number, IssueCountEntry[]> {
  const map = new Map<number, IssueCountEntry[]>();
  for (const entry of entries) {
    let arr = map.get(entry.userId);
    if (!arr) {
      arr = [];
      map.set(entry.userId, arr);
    }
    arr.push(entry);
  }
  return map;
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface WorkloadState {
  /** Which indicator type is active (null = off) */
  indicatorType: WorkloadIndicatorType | null;
  /** Derived: whether any workload overlay is visible */
  enabled: boolean;
  /** Display as absolute hours or as percentage of capacity */
  displayMode: WorkloadDisplayMode;
  /** Whether to divide hours equally among assignees */
  perPersonDistribution: boolean;
  /** Computed workload data (hours-based) */
  entries: WorkloadEntry[];
  /** Workload entries indexed by userId for O(1) lookup per resource row */
  entriesByUser: Map<number, WorkloadEntry[]>;
  /** Computed issue count data */
  issueCountEntries: IssueCountEntry[];
  /** Issue count entries indexed by userId for O(1) lookup per resource row */
  issuesByUser: Map<number, IssueCountEntry[]>;
  /** Default capacity scheme schedule (for non-working day coloring) */
  defaultSchedule: DaySchedule | null;
  /** Per-user capacity schedules (userId → day-of-week hours map) */
  userSchedules: Map<number, Record<string, number>>;
  /** Per-item total worklog hours (mondayItemId → totalHours). Used to compute remaining estimate. */
  worklogsByItem: Map<string, number>;
  /** Raw worklog entries for the visible date range (for rendering worklog events on scheduler) */
  rawWorklogs: RawWorklogEntry[];

  // ─── Actions ──────────────────────────────────────────────────────────────

  setIndicatorType: (type: WorkloadIndicatorType | null) => void;
  setEnabled: (enabled: boolean) => void;
  toggleEnabled: () => void;
  setDisplayMode: (mode: WorkloadDisplayMode) => void;
  setPerPersonDistribution: (enabled: boolean) => void;
  setEntries: (entries: WorkloadEntry[]) => void;
  setIssueCountEntries: (entries: IssueCountEntry[]) => void;
  setDefaultSchedule: (schedule: DaySchedule | null) => void;
  setUserSchedules: (schedules: Map<number, Record<string, number>>) => void;
  setWorklogsByItem: (map: Map<string, number>) => void;
  setRawWorklogs: (worklogs: RawWorklogEntry[]) => void;
  clear: () => void;
}

const persisted = loadPersistedState();

export const useWorkloadStore = create<WorkloadState>((set) => ({
  indicatorType: persisted.indicatorType,
  enabled: persisted.indicatorType !== null,
  displayMode: persisted.displayMode,
  perPersonDistribution: false,
  entries: [],
  entriesByUser: new Map(),
  issueCountEntries: [],
  issuesByUser: new Map(),
  defaultSchedule: null,
  userSchedules: new Map(),
  worklogsByItem: new Map(),
  rawWorklogs: [],

  setIndicatorType: (indicatorType) => {
    set((state) => {
      persistState(indicatorType, state.displayMode);
      return { indicatorType, enabled: indicatorType !== null };
    });
  },
  setEnabled: (enabled) =>
    set((state) => {
      const indicatorType = enabled ? (state.indicatorType ?? DEFAULT_INDICATOR_TYPE) : null;
      persistState(indicatorType, state.displayMode);
      return { enabled, indicatorType };
    }),
  toggleEnabled: () =>
    set((state) => {
      const newEnabled = !state.enabled;
      const indicatorType = newEnabled ? (state.indicatorType ?? DEFAULT_INDICATOR_TYPE) : null;
      persistState(indicatorType, state.displayMode);
      return { enabled: newEnabled, indicatorType };
    }),
  setDisplayMode: (displayMode) => {
    set((state) => {
      persistState(state.indicatorType, displayMode);
      return { displayMode };
    });
  },
  setPerPersonDistribution: (perPersonDistribution) => set({ perPersonDistribution }),
  setEntries: (entries) => set({ entries, entriesByUser: buildEntriesByUser(entries) }),
  setIssueCountEntries: (issueCountEntries) => set({ issueCountEntries, issuesByUser: buildIssuesByUser(issueCountEntries) }),
  setDefaultSchedule: (defaultSchedule) => set({ defaultSchedule }),
  setUserSchedules: (userSchedules) => set({ userSchedules }),
  setWorklogsByItem: (worklogsByItem) => set({ worklogsByItem }),
  setRawWorklogs: (rawWorklogs) => set({ rawWorklogs }),
  clear: () => set({ entries: [], entriesByUser: new Map(), issueCountEntries: [], issuesByUser: new Map(), worklogsByItem: new Map(), rawWorklogs: [] }),
}));
